import React from 'react';
import styled, { css } from 'astroturf/react';

import { Icon } from 'js/design_system/Icon';
import { Popover } from 'js/design_system/Popover';
import PremiumLock from 'js/premium/PremiumLock';
import {
  MegaMenuUtilityItem,
  megaMenuColumns,
  megaMenuUtilityItems,
} from './megaMenuItems';

const PanelRoot = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-items: flex-start;
  pointer-events: auto;
`;

const MobileCloseButton = styled.button`
  @import 'stylesheets/design_system/breakpoint';

  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  display: none;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: 0;
  background: transparent;
  color: var(--text-primary, #141413);
  cursor: pointer;

  @media (max-width: ($ds-tablet - 1px)) {
    display: inline-flex;
  }
`;

const LinksSection = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  width: 100%;
  box-sizing: border-box;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: center;
  gap: 40px;
  padding: 40px 48px 64px;
  background: var(--background-white, #fff);
  border-bottom: 1px solid var(--stroke-light, #dddde2);

  @media (max-width: ($ds-tablet - 1px)) {
    padding: 16px 16px 24px;
  }
`;

const ColumnsRow = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  flex: 1 0 0;
  min-width: 0;
  max-width: 1080px;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  row-gap: 40px;

  @include breakpoint(small) {
    flex-direction: column;
    flex-wrap: nowrap;
    justify-content: flex-start;
    row-gap: 32px;
  }
`;

const Column = styled.div`
  display: flex;
  width: 204px;
  flex-direction: column;
  align-items: flex-start;
`;

const ColumnHeading = styled.h2`
  @import 'stylesheets/design_system/font';
  @include body__large;

  margin: 0;
  padding: 0 0 8px 4px;
  font-weight: var(--type-body-lg-bold-font-weight, 600);
  color: var(--text-primary, #141413);
`;

const ItemRow = styled.a`
  @import 'stylesheets/design_system/font';
  @include body__medium;

  display: flex;
  width: 100%;
  box-sizing: border-box;
  height: 32px;
  align-items: center;
  gap: 8px;
  padding: 0 4px;
  border-radius: 4px;
  line-height: var(--type-body-md-line-height, 20px);
  color: var(--text-primary, #141413);
  text-decoration: none;

  &:hover,
  &:visited {
    color: var(--text-primary, #141413);
    text-decoration: none;
  }

  &:hover {
    background: var(--background-light-blue, #eef2f9);
  }
`;

const ItemLabel = styled.span`
  flex: 1 0 0;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const LockSlot = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
`;

const UtilitySection = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  width: 100%;
  box-sizing: border-box;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 40px;
  padding: 16px 64px;
  background: var(--background-white, #fff);
  border-bottom: 1px solid var(--stroke-light, #dddde2);

  @media (max-width: ($ds-tablet - 1px)) {
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: flex-start;
    gap: 8px;
    padding: 16px;
  }
`;

const UtilityRow = styled.a`
  @import 'stylesheets/design_system/font';
  @include body__medium;

  display: flex;
  box-sizing: border-box;
  height: 32px;
  align-items: center;
  gap: 8px;
  padding: 0 4px;
  border-radius: 4px;
  line-height: var(--type-body-md-line-height, 20px);
  color: var(--text-primary, #141413);
  text-decoration: none;

  &:hover,
  &:visited {
    color: var(--text-primary, #141413);
    text-decoration: none;
  }

  &:hover {
    background: var(--background-light-blue, #eef2f9);
  }
`;

const Scrim = styled.button`
  flex: 1 0 0;
  min-height: 1px;
  width: 100%;
  padding: 0;
  border: 0;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(2px);
  cursor: pointer;
`;

const fullBleedContainer = css`
  @import 'stylesheets/design_system/breakpoint';

  position: fixed !important;
  bottom: 0;
  left: 0 !important;
  width: 100% !important;
  max-width: none !important;
  max-height: none !important;
  pointer-events: none;

  @media (max-width: ($ds-tablet - 1px)) {
    top: 0 !important;
    padding-top: 0 !important;
  }
`;

const UtilityIcon = ({ item }: { item: MegaMenuUtilityItem }) => {
  if (item.icon === 'premium-lock') {
    return <PremiumLock width={20} />;
  }
  return <Icon name={item.icon} size={20} />;
};

export type AppsMegaMenuPanelProps = {
  isPremium: boolean;
  onScrimClick?: () => void;
};

export const AppsMegaMenuPanel = ({
  isPremium,
  onScrimClick,
}: AppsMegaMenuPanelProps) => (
  <PanelRoot data-testid="apps-mega-menu">
    {onScrimClick != null && (
      <MobileCloseButton
        type="button"
        aria-label="Close menu"
        onClick={onScrimClick}
        data-testid="mega-menu-close"
      >
        <Icon name="close" size={24} aria-hidden />
      </MobileCloseButton>
    )}
    <LinksSection>
      <ColumnsRow>
        {megaMenuColumns(isPremium).map((column) => (
          <Column key={column.heading} data-testid="mega-menu-column">
            <ColumnHeading data-testid="mega-menu-heading">
              {column.heading}
            </ColumnHeading>
            {column.items.map((item) => (
              <ItemRow
                key={item.label}
                href={item.href}
                data-testid="mega-menu-item"
              >
                <ItemLabel data-testid="mega-menu-item-label">
                  {item.label}
                </ItemLabel>
                {item.locked && !isPremium && (
                  <LockSlot data-testid="mega-menu-item-lock">
                    <PremiumLock width={18} />
                  </LockSlot>
                )}
              </ItemRow>
            ))}
          </Column>
        ))}
      </ColumnsRow>
    </LinksSection>
    <UtilitySection>
      {megaMenuUtilityItems(isPremium).map((item) => (
        <UtilityRow
          key={item.label}
          href={item.href}
          data-testid="mega-menu-utility-item"
        >
          <UtilityIcon item={item} />
          {item.label}
        </UtilityRow>
      ))}
    </UtilitySection>
    <Scrim
      type="button"
      aria-label="Close menu"
      data-testid="mega-menu-scrim"
      onClick={onScrimClick}
    />
  </PanelRoot>
);

export type AppsMegaMenuProps = {
  open: boolean;
  isPremium: boolean;
  onChange?: (open: boolean) => void;
  className?: string;
  children: React.ReactNode;
};

const AppsMegaMenu = ({
  open,
  isPremium,
  onChange,
  className,
  children,
}: AppsMegaMenuProps) => (
  <Popover
    open={open}
    placement="bottom-start"
    offset={1}
    disableFlip
    disableShift
    className={className}
    containerClassName={fullBleedContainer}
    render={() => (
      <AppsMegaMenuPanel
        isPremium={isPremium}
        onScrimClick={() => onChange?.(false)}
      />
    )}
    onChange={onChange}
  >
    {children}
  </Popover>
);

export default AppsMegaMenu;
